import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Star, MapPin, ArrowRight, Wifi, ChefHat, Snowflake, Tv, WashingMachine,
  Coffee, Bath, KeyRound, ShieldCheck, BedDouble, Calendar, Users, Clock,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingWidget from "@/components/BookingWidget";
import MobileBottomCTA from "@/components/MobileBottomCTA";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import { useT } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  wifi: Wifi, "chef-hat": ChefHat, snowflake: Snowflake, tv: Tv,
  "washing-machine": WashingMachine, coffee: Coffee, bath: Bath,
  "key-round": KeyRound, "shield-check": ShieldCheck, "bed-double": BedDouble,
};

export default function ApartmentDetail() {
  const { t } = useT();
  const { slug } = useParams();
  const [apt, setApt] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    let active = true;
    axios.get(`${API}/apartments/${slug}`)
      .then((r) => active && setApt(r.data))
      .catch(() => active && setApt(null));
    return () => { active = false; };
  }, [slug]);

  if (!apt) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <p className="text-3xl font-bold text-slate-900 tracking-tight">…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 pb-20 lg:pb-0">
      <Navbar />

      {/* HEADER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-28 md:pt-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-3">{t("apt.the_apartment")}</p>
          <h1 data-testid="apt-title" className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 display-tight-2 max-w-4xl">
            {apt.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><Star size={15} className="text-navy-600 fill-navy-600" /> {apt.rating} · {apt.reviews_count} {t("apt.reviews_label")}</span>
            <span className="flex items-center gap-1.5"><MapPin size={15} className="text-navy-600" /> {apt.location}</span>
          </div>
        </Reveal>

        {/* GALLERY */}
        <Reveal>
          <div className="mt-8 relative">
            <div className="grid grid-cols-4 grid-rows-2 gap-3 h-[360px] md:h-[520px]">
              <button onClick={() => setLightbox({ open: true, index: 0 })} className="col-span-4 md:col-span-2 row-span-2 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-600">
                <img src={apt.gallery[0]} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
              </button>
              {apt.gallery.slice(1, 5).map((src, i) => (
                <button key={i} onClick={() => setLightbox({ open: true, index: i + 1 })} className="hidden md:block col-span-1 row-span-1 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-600">
                  <img src={src} alt="" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500" />
                </button>
              ))}
            </div>
            <button
              onClick={() => setLightbox({ open: true, index: 0 })}
              data-testid="detail-show-all"
              className="absolute bottom-4 right-4 bg-white shadow-soft hover:shadow-elevate text-slate-900 text-sm font-semibold rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-all"
            >
              {t("gal.show_all")}
            </button>
          </div>
        </Reveal>
      </section>

      {/* CONTENT + STICKY CARD */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">L&apos;appartamento</h2>
            <p className="text-slate-600 leading-relaxed mt-5 text-base">{apt.long_description}</p>
          </Reveal>

          <Reveal>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mt-16">{t("apt.services")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
              {apt.amenities.map((a) => {
                const Icon = ICON_MAP[a.icon] || ChefHat;
                return (
                  <div key={a.label} className="flex items-center gap-3 p-4 rounded-xl bg-white border border-cream-100">
                    <Icon size={18} className="text-navy-700" />
                    <span className="text-sm text-slate-900">{a.label}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mt-16">{t("apt.rules")}</h2>
            <ul className="mt-6 space-y-3">
              {apt.house_rules.map((r) => (
                <li key={r} className="flex items-start gap-3 text-slate-800 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-navy-600 mt-2" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mt-16">{t("apt.checkin_out")}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="p-6 rounded-2xl bg-white border border-cream-100">
                <Clock size={18} className="text-navy-700 mb-3" />
                <p className="font-medium text-slate-900">{t("bw.checkin")}</p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{apt.checkin_info}</p>
              </div>
              <div className="p-6 rounded-2xl bg-white border border-cream-100">
                <Clock size={18} className="text-navy-700 mb-3" />
                <p className="font-medium text-slate-900">{t("bw.checkout")}</p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{apt.checkout_info}</p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mt-16">{t("apt.position")}</h2>
            <div className="mt-6 rounded-2xl overflow-hidden border border-cream-200">
              <iframe
                title="Mappa appartamento"
                src="https://www.openstreetmap.org/export/embed.html?bbox=9.205%2C45.483%2C9.220%2C45.490&layer=mapnik&marker=45.4862%2C9.2113"
                className="w-full h-[360px] border-0"
                loading="lazy"
              />
            </div>
            <ul className="mt-5 space-y-2 text-sm text-slate-800">
              {apt.nearby.map((n) => (
                <li key={n} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-navy-600 mt-2" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mt-16">{t("apt.faq")}</h2>
            <Accordion type="single" collapsible className="mt-6">
              {apt.faqs.map((f, i) => (
                <AccordionItem key={i} value={`fi-${i}`} className="border-cream-200">
                  <AccordionTrigger className="text-left text-lg text-slate-900 hover:text-navy-700 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-[15px]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>

        {/* STICKY BOOKING CARD */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div data-testid="sticky-booking-card" className="bg-white border border-cream-100 rounded-2xl p-7 shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
              <div className="flex items-baseline justify-between">
                <p className="text-3xl font-bold text-slate-900 tracking-tight">€ {apt.price_per_night}<span className="text-base font-sans text-slate-600"> / {t("preview.per_night")}</span></p>
                <p className="text-sm text-slate-600 flex items-center gap-1.5">
                  <Star size={13} className="text-navy-600 fill-navy-600" /> {apt.rating}
                </p>
              </div>
              <div className="mt-5">
                <BookingWidget variant="card" />
              </div>
              <div className="mt-6 space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Users size={14} className="text-navy-600" /> {t("preview.up_to")} {apt.max_guests} {t("bw.guest_many")}</p>
                <p className="flex items-center gap-2"><Calendar size={14} className="text-navy-600" /> {t("apt.free_cancel")}</p>
              </div>
              <Link
                to="/prenotazione"
                data-testid="sticky-card-cta"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy-600 hover:bg-navy-700 text-white px-6 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
              >
                Prenota ora <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </aside>
      </section>

      <Footer />
      <MobileBottomCTA price={apt.price_per_night} />
      <Lightbox
        images={apt.gallery}
        startIndex={lightbox.index}
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </div>
  );
}
